import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import ThemeToggle from '../components/ThemeToggel';

function NotFound() {
  const gridItems = useRef([]);
  const [theme, setTheme] = React.useState('light'); // Changed default to light
  const particleCount = 100; // Increased particle count
  
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.set(gridItems.current, { 
        opacity: 0, 
        y: 30,
        scale: 0.95
      });
      
      const tl = gsap.timeline();
      
      tl.to(gridItems.current, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.8,
        stagger: 0.1,
        ease: "power3.out"
      })
      .to("#title404", {
        y: -20,
        duration: 2,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut"
      }, "-=0.5")
      .to(".circle-decoration", {
        rotation: 360,
        duration: 20,
        repeat: -1,
        ease: "none"
      }, "<");

      // Enhanced infinite particle animation
      gsap.to(".particle", {
        y: (i) => -300 - (Math.random() * 300),
        x: (i) => (Math.random() - 0.5) * 100,
        opacity: (i) => Math.random() * 0.8,
        duration: "random(3, 7)",
        stagger: {
          each: 0.05,
          repeat: -1,
          yoyo: false,
          repeatRefresh: true,
          from: "random"
        },
        ease: "none",
        onRepeat: function(target) {
          // Reset particle position when animation completes
          gsap.set(target.targets()[0], {
            y: 800 + (Math.random() * 100),
            x: Math.random() * window.innerWidth,
            opacity: Math.random() * 0.8
          });
        }
      });

      // Add staggered animation for 404 and ERROR text
      gsap.to(["#title404", "#errorText"], {
        y: -20,
        duration: 2,
        repeat: -1,
        yoyo: true,
        stagger: 0.2,
        ease: "power1.inOut"
      }, "-=0.5");
    });
    
    return () => ctx.revert();
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    document.documentElement.className = newTheme;
  };

  const particles = [...Array(particleCount)].map((_, i) => (
    <div
      key={i}
      className={`particle absolute rounded-full ${
        theme === 'dark' ? 'bg-white' : 'bg-gray-400'
      }`}
      style={{
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100 + 100}%`, // Start below viewport
        width: `${Math.random() * 6 + 1}px`, // Larger size variation
        height: `${Math.random() * 6 + 1}px`,
        opacity: Math.random() * 0.5,
        transform: `scale(${Math.random() * 2})`,
        filter: `blur(${Math.random() * 2}px)` // Add subtle blur for depth
      }}
    />
  ));

  return (
    <>
      <Helmet>
        <title>404 - Page Not Found</title>
        <meta name="description" content="The requested page couldn't be found." />
      </Helmet>
      <div className={`min-h-screen w-full flex items-center justify-center p-8 relative ${
        theme === 'dark' 
          ? 'bg-[#0a0a0a] text-white' 
          : 'bg-gray-100 text-gray-900'
      } overflow-hidden transition-colors duration-300`}>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {particles}
        </div>
        <div className="w-full max-w-5xl mx-auto grid grid-cols-6 gap-4 relative z-10">
          {/* Main 404 Card */}
          <div 
            ref={el => gridItems.current[0] = el}
            className={`col-span-6 p-12 rounded-3xl flex flex-col items-center gap-8 ${
              theme === 'dark'
                ? 'bg-gradient-to-br from-purple-600/20 to-blue-600/20 border-white/10'
                : 'bg-gradient-to-br from-purple-100 to-blue-100 border-gray-200'
            } backdrop-blur-sm border relative group hover:border-opacity-20 transition-all duration-300`}
          >
            <div className="circle-decoration absolute -top-20 -right-20 w-40 h-40 border-2 border-dashed border-purple-500/20 rounded-full" />
            <div className="flex flex-col items-center gap-4">
              <h1 
                id="title404"
                className={`text-8xl font-bold bg-clip-text text-transparent ${
                theme === 'dark'
                  ? 'bg-gradient-to-r from-purple-400 to-blue-400'
                  : 'bg-gradient-to-r from-purple-600 to-blue-600'
              }`}>
                404
              </h1>
              <span 
                id="errorText"
                className={`text-2xl font-bold tracking-widest ${
                  theme === 'dark'
                    ? 'text-white/60'
                    : 'text-gray-600'
                }`}
              >
                ERROR
              </span>
            </div>
            
          </div>

          {/* Description Card */}
          <div 
            ref={el => gridItems.current[1] = el}
            className={`col-span-2 p-8 rounded-3xl ${
              theme === 'dark'
                ? 'bg-white/5 border-white/10'
                : 'bg-gray-200 border-gray-200'
            } backdrop-blur-sm border hover:border-opacity-20 transition-all duration-300`}
          >
            <h2 className={`text-2xl font-medium ${
              theme === 'dark'
                ? 'text-white/90'
                : 'text-gray-900'
            } mb-2`}>
              Oops! Page Not Found
            </h2>
            <p className={`${
              theme === 'dark'
                ? 'text-white/60'
                : 'text-gray-700'
            }`}>
              The page you're looking for has vanished into the digital void.
            </p>
          </div>

          {/* Theme Toggle */}
          <div 
            ref={el => gridItems.current[2] = el}
            className="col-span-2 flex items-center justify-center"
          >
            <ThemeToggle 
              checked={theme === 'dark'} 
              onChange={toggleTheme}
            />
          </div>

          {/* Interactive Card */}
          <div 
            ref={el => gridItems.current[3] = el}
            className={`col-span-2 p-8 rounded-3xl ${
              theme === 'dark'
                ? 'bg-gradient-to-br from-blue-600/20 to-purple-600/20 border-white/10'
                : 'bg-gradient-to-br from-blue-100 to-purple-100 border-gray-200'
            } backdrop-blur-sm border hover:border-opacity-20 transition-all duration-300`}
          >
            <div className="h-full flex items-center justify-center">
              <Link
                to="/"
                className={`group relative px-6 py-3 ${
                  theme === 'dark'
                    ? 'bg-white/10 hover:bg-white/15'
                    : 'bg-gray-200 hover:bg-gray-300'
                } rounded-xl transition-all duration-300`}
              >
                <span className="relative z-10">Return Home</span>
                <div className={`absolute inset-0 ${
                  theme === 'dark'
                    ? 'bg-gradient-to-r from-purple-500 to-blue-500'
                    : 'bg-gradient-to-r from-purple-300 to-blue-300'
                } opacity-0 group-hover:opacity-20 rounded-xl transition-opacity duration-300`} />
              </Link>
            </div>
          </div>

          {/* Pattern Card */}
          <div 
            ref={el => gridItems.current[4] = el}
            className={`col-span-6 h-24 rounded-3xl ${
              theme === 'dark'
                ? 'bg-white/5 border-white/10'
                : 'bg-gray-200 border-gray-200'
            } backdrop-blur-sm border overflow-hidden`}
          >
            <div className="w-full h-full grid grid-cols-12 gap-4 opacity-20">
              {[...Array(12)].map((_, i) => (
                <div key={i} className={`${
                  theme === 'dark'
                    ? 'bg-gradient-to-b from-purple-500/50 to-blue-500/50'
                    : 'bg-gradient-to-b from-purple-300 to-blue-300'
                } h-full`} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default NotFound;